<template>
  <div>
    <Head :title="$t('message.expense_tickets.expense_tickets', 2)" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/expense/expense-tickets">
        {{ $t('message.expense_tickets.expense_tickets', 2) }}
      </Link>
      <span class="text-indigo-400 font-medium">/</span> {{ $t('message.general.create') }}
    </h1>
    <div class="flex justify-between">
      <div>
        <div class="max-w-4xl bg-white rounded-md shadow overflow-hidden">
          <form @submit.prevent="store">
            <div class="flex flex-wrap -mb-8 -mr-6 p-8">
              <text-input v-model="form.expense_line" :error="form.errors.expense_line" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.expense_tickets.expense_line')" />
              <text-input v-model="form.in_favour_of" :error="form.errors.in_favour_of" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.expense_tickets.in_favor_of')" />

              <div class="mb-6 mr-6 p-4 w-full bg-white rounded-md shadow overflow-x-auto">
                <table class="mb-6 w-full whitespace-nowrap">
                  <thead>
                    <tr class="text-left font-bold">
                      <th class="pb-4 pt-6 px-6">
                        {{ $t('message.expense_tickets.requested_by') }}
                      </th>
                      <th class="pb-4 pt-6 px-6">
                        {{ $t('message.general.department') }}
                      </th>
                      <th class="pb-4 pt-6 px-6">
                        {{ $t('message.expense_tickets.title') }}
                      </th>
                      <th class="pb-4 pt-6 px-6">
                        {{ $t('message.expense_tickets.total') }}
                      </th>
                      <th class="pb-4 pt-6 px-6" />
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(expense, index) in form.expenses" :key="expense.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
                      <td class="border-t">
                        <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="openEditModal(index)">
                          {{ expense.requestingStaff.name }}
                        </div>
                      </td>
                      <td class="border-t">
                        <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="openEditModal(index)">
                          {{ $t(`message.departments.${slugifyDepartment(expense.department.name)}`) }}
                        </div>
                      </td>
                      <td class="border-t">
                        <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="openEditModal(index)">
                          {{ expense.title }}
                        </div>
                      </td>
                      <td class="border-t">
                        <div class="flex items-center px-6 py-4 focus:text-indigo-500" @click="openEditModal(index)">
                          {{ formatPrice(expense.total) }}
                        </div>
                      </td>

                      <td class="w-px border-b border-t">
                        <button class="flex items-center px-4 focus:text-indigo-500" @click.prevent="removeRow(index)">
                          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="form.expenses.length > 0" class="text-left font-bold">
                      <td class="pb-4 pt-6 px-6 border-t" colspan="3">{{ $t('message.expense_tickets.total') }}:</td>
                      <td class="pb-4 pt-6 px-6 border-t">{{ formatPrice(form.total) }}</td>
                      <td class="pb-4 pt-6 px-6 border-t" />
                    </tr>
                    <tr v-if="form.expenses.length > 0 && form.total > 0">
                      <td class="pb-4 pt-6 px-6 border-t">{{ $t('message.expense_tickets.amount_in_words') }}:</td>
                      <td class="pb-4 pt-6 px-6 text-right focus:text-indigo-500 border-t" colspan="3">{{ capitalisedFirstLetter(amountInWords(form.total)) }} {{ this.currency_name }}</td>
                      <td class="pb-4 pt-6 px-6 border-t" />
                    </tr>
                  </tbody>
                </table>

                <button class="btn-indigo py-4 w-full" @click.prevent="openExpenseModal">
                  {{ $t('message.expense_tickets.add_row') }}
                </button>
              </div>

              <text-input v-model="form.to_be_received_by" :error="form.errors.to_be_received_by" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.expense_tickets.to_be_received_by')" />
              <text-input v-model="form.nafa_code" :error="form.errors.nafa_code" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.expense_tickets.nafa_code')" />
              <select-input v-model="form.payment_type_id" :error="form.errors.payment_type_id" class="lg pb-8 pr-6 w-full" :label="$t('message.expense_tickets.payment_mode')">
                <option :value="null" />
                <option v-for="payment_mode in payment_modes" :key="payment_mode.id" :value="payment_mode.id">
                  {{ $t(`message.expense_tickets.${slugifyPaymentMode(payment_mode.name)}`) }}
                </option>
              </select-input>
              <select-input v-if="form.payment_type_id == 1 || form.payment_type_id == 3" v-model="form.bank_account_name" :error="form.errors.bank_account_name" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.expense_tickets.bank_name')">
                <option :value="null" />
                <option v-for="bank in banks" :key="bank.id" :value="bank.name">{{ bank.name }}</option>
              </select-input>
              <text-input v-if="form.payment_type_id == 1 && form.bank_account_name" v-model="form.bank_account_number" :error="form.errors.bank_account_number" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.expense_tickets.account_number')" />
              <text-input v-if="form.payment_type_id == 3 && form.bank_account_name" v-model="form.cheque_number" :error="form.errors.cheque_number" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.expense_tickets.cheque_number')" />
              <text-input v-if="form.payment_type_id == 4" v-model="form.wallet_number" :error="form.errors.wallet_number" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.expense_tickets.wallet_number')" />
            </div>
            <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
              <loading-button :loading="form.processing" class="btn-indigo" type="submit">
                {{ $t('message.expense_tickets.create_expense_ticket') }}
              </loading-button>
            </div>
          </form>
        </div>
      </div>
      <div class="lg:ml-4">
        <div v-for="(file, index) in form.photos.length + 1" :key="index">
          <image-input v-model="files[index]" :image_path="image_paths[index]" :canUpdate="canUpdate" :error="form.errors.photos" class="pb-8 pr-6" type="file" accept="image/*" :label="$t('message.expense_tickets.receipt_if_any')" />
        </div>
        <progress v-if="form.progress" :value="form.progress.percentage" max="100">{{ form.progress.percentage }}%</progress>
      </div>
    </div>

    <transition enter-active-class="transition ease-out duration-100" enter-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition ease-in duration-75" leave-class="opacity-100" leave-to-class="opacity-0">
      <div v-if="isModalOpen" class="fixed z-10 inset-0 overflow-y-auto">
        <div class="flex items-end justify-center pb-20 pt-4 px-4 min-h-screen text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true">
            <div class="absolute inset-0 bg-gray-500 opacity-75" />
          </div>
          <div class="inline-block align-bottom text-left bg-white rounded-lg shadow-xl overflow-hidden transform transition-all sm:align-middle sm:my-8 sm:w-full sm:max-w-lg">
            <div class="flex justify-end pt-4 px-4">
              <button class="text-gray-500 hover:text-gray-700 focus:text-gray-700 focus:outline-none" @click="closeModal">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="pb-4 pt-5 px-4 bg-white sm:p-6 sm:pb-4">
              <div class="sm:flex sm:items-start">
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <div class="mt-2">
                    <form @submit.prevent="addRow">
                      <div class="flex flex-wrap -mb-4">
                        <select-input v-model="newItem" class="lg:w pb-8 pr-6 w-full" :label="$t('message.expense_tickets.select_expense')">
                          <option :value="null" />
                          <option v-for="expense in local_expenses" :key="expense.id" :value="expense">{{ expense.title }} {{ $t('message.general.by') }} {{ expense.requestingStaff.name }} ({{ $t(`message.departments.${slugifyDepartment(expense.department.name)}`) }}) - {{ expense.date }}</option>
                        </select-input>
                        <button class="btn-indigo mb-4 mr-6 px-6 w-full" @click.prevent="addRow()">
                          {{ $t('message.expense_tickets.add_expense') }}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput.vue'
import LoadingButton from '@/Shared/LoadingButton'
import ImageInput from '@/Shared/ImageInput'
import SelectInput from '@/Shared/SelectInput'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TextareaInput,
    TextInput,
    SelectInput,
    ImageInput,
  },
  layout: Layout,
  remember: 'form',
  props: {
    payment_modes: Array,
    expenses: Array,
    banks: Array,
  },
  data() {
    return {
      form: this.$inertia.form({
        expense_line: null,
        in_favour_of: null,
        expenses: [],
        total: null,
        nafa_code: null,
        payment_type_id: null,
        bank_account_name: null,
        bank_account_number: null,
        cheque_number: null,
        wallet_number: null,
        to_be_received_by: null,
        image_path: '',
        photos: [],
        prepared_by: this.$page.props.auth.user.id,
      }),
      local_expenses: this.expenses,
      currency_name: this.$page.props.auth.user.account.currency_name,
      newItem: this.$inertia.form({
        id: null,
        title: null,
        total: null,
        requestingStaff: null,
        department: null,
      }),
      isModalOpen: false,
      isEdit: false,
      currentIndex: null,
      files: [],
      image_paths: [],
    }
  },
  watch: {
    files: {
      handler(files) {
        this.form.photos = files.filter((file) => file != null)
        files.forEach((file, index) => {
          if (file == null) {
            this.delete(this.files, this.image_paths, index)
          }
        })
      },
      deep: true,
    },
  },
  methods: {
    openExpenseModal() {
      this.isEdit = false
      this.isModalOpen = true
    },

    openEditModal(index) {
      this.isEdit = true
      this.newItem.name = this.form.expenses[index].name
      this.newItem.description = this.form.expenses[index].description
      this.newItem.cost = this.form.expenses[index].cost
      this.newItem.quantity = this.form.expenses[index].quantity
      this.newItem.sub_total = this.form.expenses[index].sub_total
      this.currentIndex = index
      this.isModalOpen = true
    },

    closeModal() {
      this.isEdit = false
      this.newItem = {}
      this.isModalOpen = false
    },

    addRow() {
      if (Object.keys(this.newItem).length === 0) {
        this.newItem.errors.title = 'Select one expense'
      }

      // check if the item already exists using the id
      let exists = false
      for (let i = 0; i < this.form.expenses.length; i++) {
        if (this.form.expenses[i].id === this.newItem.id) {
          exists = true
          break
        }
      }

      if (this.newItem.title != null && this.newItem.total != null && !exists) {
        this.form.expenses.push({
          id: this.newItem.id,
          title: this.newItem.title,
          total: this.newItem.total,
          requestingStaff: this.newItem.requestingStaff,
          department: this.newItem.department,
        })

        // remove that expense in the list of expenses
        this.local_expenses = this.local_expenses.filter((expense) => expense.id != this.newItem.id)

        this.newItem = {}
        this.calculateTotal()
        this.closeModal()
      }
    },

    removeRow(index) {
      this.local_expenses.push(this.form.expenses[index])
      this.form.expenses.splice(index, 1)
      this.calculateTotal()
    },

    editRow() {
      if (this.newItem.name == null) {
        this.newItem.errors.name = 'Name is required'
      }
      if (this.newItem.quantity == null) {
        this.newItem.errors.quantity = 'Quantity is required'
      }
      if (this.newItem.name != null && this.newItem.quantity != null) {
        if (this.newItem.cost != null && this.newItem.cost >= 0 && this.newItem.quantity != null && this.newItem.quantity >= 0) {
          this.newItem.sub_total = this.newItem.cost * this.newItem.quantity
        } else {
          this.newItem.sub_total = null
        }
        var editedItem = {
          name: this.newItem.name,
          description: this.newItem.description,
          cost: this.newItem.cost,
          quantity: this.newItem.quantity,
          sub_total: this.newItem.sub_total,
        }
        let index = this.currentIndex
        this.form.items[index] = editedItem
      }

      this.newItem.name = null
      this.newItem.description = null
      this.newItem.cost = null
      this.newItem.quantity = null
      this.newItem.sub_total = null

      this.currentIndex = null

      this.calculateTotal()
      this.isEdit = false
      this.closeModal()
    },

    capitalisedFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },

    calculateTotal() {
      let total = 0
      for (let i = 0; i < this.form.expenses.length; i++) {
        if (this.form.expenses[i].total != null) {
          total += this.form.expenses[i].total
        } else {
          total += 0
        }
      }
      this.form.total = total
    },

    formatPrice(price) {
      const currency = this.$page.props.auth.user.account.currency
      return new Intl.NumberFormat('en-GM', { style: 'currency', currency }).format(price)
    },

    amountInWords(amount) {
      const locale = this.$i18n.locale
      if (locale == 'en') {
        const units = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
        const teens = ['eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen']
        const tens = ['ten', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

        function convertBelowThousand(num) {
          if (num === 0) {
            return ''
          } else if (num < 10) {
            return units[num]
          } else if (num < 20) {
            return teens[num - 11]
          } else if (num < 100) {
            const digit = num % 10
            const ten = Math.floor(num / 10)
            return `${tens[ten - 1]}${digit !== 0 ? ' ' + units[digit] : ''}`
          } else if (num < 1000) {
            const hundred = Math.floor(num / 100)
            const remainder = num % 100
            return `${units[hundred]} hundred${remainder !== 0 ? ' ' + convertBelowThousand(remainder) : ''}`
          } else if (num < 1000000) {
            const thousand = Math.floor(num / 1000)
            const remainder = num % 1000
            return `${convertBelowThousand(thousand)} thousand${remainder !== 0 ? ' ' + convertBelowThousand(remainder) : ''}`
          } else {
            return 'Amount is too large for this function'
          }
        }

        function convertBelowMillion(num) {
          const thousand = Math.floor(num / 1000)
          const remainder = num % 1000

          return `${convertBelowThousand(thousand)} thousand${remainder !== 0 ? ' ' + convertBelowThousand(remainder) : ''}`
        }

        function convertBelowBillion(num) {
          const million = Math.floor(num / 1000000)
          const remainder = num % 1000000

          return `${convertBelowThousand(million)} million${remainder !== 0 ? ' ' + convertBelowMillion(remainder) : ''}`
        }

        if (amount === 0) {
          return 'zero'
        } else if (amount < 0) {
          return 'negative ' + amountInWords(-amount)
        } else if (amount < 1000) {
          return convertBelowThousand(amount)
        } else if (amount < 1000000) {
          return convertBelowMillion(amount)
        } else if (amount < 1000000000) {
          return convertBelowBillion(amount)
        } else {
          return 'Amount is too large for this function'
        }
      } else if (locale == 'fr') {
        const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf']
        const teens = ['onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf']
        const tens = ['dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix', 'quatre-vingt', 'quatre-vingt-dix']

        function convertBelowThousand(num) {
          if (num === 0) {
            return ''
          } else if (num < 10) {
            return units[num]
          } else if (num < 20) {
            return teens[num - 11]
          } else if (num < 100) {
            const digit = num % 10
            const ten = Math.floor(num / 10)
            return `${tens[ten - 1]}${digit !== 0 ? ' ' + units[digit] : ''}`
          } else if (num < 1000) {
            const hundred = Math.floor(num / 100)
            const remainder = num % 100
            return `${units[hundred]} cent${remainder !== 0 ? ' ' + convertBelowThousand(remainder) : ''}`
          } else if (num < 1000000) {
            const thousand = Math.floor(num / 1000)
            const remainder = num % 1000
            return `${convertBelowThousand(thousand)} mille${remainder !== 0 ? ' ' + convertBelowThousand(remainder) : ''}`
          } else {
            return 'Montant trop grand pour cette fonction'
          }
        }

        function convertBelowMillion(num) {
          const thousand = Math.floor(num / 1000)
          const remainder = num % 1000

          return `${convertBelowThousand(thousand)} mille${remainder !== 0 ? ' ' + convertBelowThousand(remainder) : ''}`
        }

        function convertBelowBillion(num) {
          const million = Math.floor(num / 1000000)
          const remainder = num % 1000000

          return `${convertBelowThousand(million)} million${remainder !== 0 ? ' ' + convertBelowMillion(remainder) : ''}`
        }

        if (amount === 0) {
          return 'zéro'
        } else if (amount < 0) {
          return 'négatif ' + amountInWords(-amount)
        } else if (amount < 1000) {
          return convertBelowThousand(amount)
        } else if (amount < 1000000) {
          return convertBelowMillion(amount)
        } else if (amount < 1000000000) {
          return convertBelowBillion(amount)
        } else {
          return 'Montant trop grand pour cette fonction'
        }
      }
    },

    slugifyDepartment(department) {
      if (department.charAt(department.length - 1) == '.') {
        department = department.slice(0, -1)
      }
      return department.toLowerCase().replace(/\./g, '').replace(/&/g, 'and').replace(/\s+/g, '_')
    },

    slugifyPaymentMode(paymentMode) {
      return paymentMode.toLowerCase().replace(' ', '_')
    },

    slugifyStatus(status) {
      return status.toLowerCase().replace(' ', '_')
    },

    delete(items, image_paths, index) {
      items.splice(index, 1)
      image_paths.splice(index, 1)
    },

    store() {
      this.form.clearErrors()

      if (this.form.payment_type_id == 1 && this.form.bank_account_number == null) {
        this.form.errors.bank_account_number = 'Account Number is required'
        return
      }
      if (this.form.payment_type_id == 3 && this.form.cheque_number == null) {
        this.form.errors.cheque_number = 'Cheque Number is required'
        return
      }
      if (this.form.payment_type_id == 4 && this.form.wallet_number == null) {
        this.form.errors.wallet_number = 'Wallet Number is required'
        return
      }
      this.form.post('/expense/expense-tickets')
    },
  },
}
</script>
